import React, { useMemo, useState } from 'react'

const toNum = (v, fallback = 0) => {
    if (typeof v === 'string') v = v.replace(',', '.')
    const n = Number(v)
    return Number.isFinite(n) ? n : fallback
}

const clamp = (n, min, max) => Math.min(max, Math.max(min, n))

const normalizeAdvancedValue = (value) => {
    const src = value && typeof value === 'object' ? value : {}

    return {
        repeat: Array.isArray(src.repeat) && src.repeat.length >= 2
            ? [
                clamp(toNum(src.repeat[0], 1), 0.01, 1000),
                clamp(toNum(src.repeat[1], 1), 0.01, 1000),
            ]
            : [1, 1],
        offset: Array.isArray(src.offset) && src.offset.length >= 2
            ? [
                clamp(toNum(src.offset[0], 0), -10, 10),
                clamp(toNum(src.offset[1], 0), -10, 10),
            ]
            : [0, 0],
        rotation: clamp(toNum(src.rotation, 0), -6.28318, 6.28318),
    }
}

const TextureTuningControls = ({
    mode = 'density',
    value = '1',
    onValueChange,
    onValueCommit,
    onReset,
    advancedValue,
    onAdvancedChange,
    disabled = false,
    className = '',
}) => {
    const [advancedOpen, setAdvancedOpen] = useState(false)

    const safeAdvanced = useMemo(() => normalizeAdvancedValue(advancedValue), [advancedValue])

    const title = mode === 'density' ? 'Плотность' : 'Масштаб'
    const hint = mode === 'density' ? 'больше = мельче' : '1 = нормально, больше = мельче'

    return (
        <div className={className}>
            <div className="custom-object-panel__scale">
                <div className="custom-object-panel__scaleTop">
                    <div className="custom-object-panel__scaleLabel">
                        {title} <span>{hint}</span>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            className="custom-object-panel__btn custom-object-panel__btn--small"
                            onClick={() => setAdvancedOpen((prev) => !prev)}
                        >
                            {advancedOpen ? 'Скрыть точно' : 'Точно'}
                        </button>

                        <button
                            type="button"
                            className="custom-object-panel__btn custom-object-panel__btn--small"
                            onClick={onReset}
                        >
                            Reset
                        </button>
                    </div>
                </div>

                <div className="custom-object-panel__scaleRow">
                    <input
                        type="range"
                        min="0.1"
                        max="12"
                        step="0.1"
                        value={String(clamp(toNum(value, 1), 0.1, 12))}
                        onChange={(e) => onValueChange?.(e.target.value)}
                        onPointerUp={(e) => onValueCommit?.(e.target.value)}
                        onKeyUp={(e) => onValueCommit?.(e.target.value)}
                        disabled={disabled}
                    />

                    <input
                        className="custom-object-panel__scaleInput"
                        value={value}
                        onChange={(e) => onValueChange?.(e.target.value)}
                        onBlur={(e) => onValueCommit?.(e.target.value)}
                        disabled={disabled}
                    />
                </div>

                {advancedOpen ? (
                    <div
                        style={{
                            display: 'grid',
                            gap: '8px',
                            marginTop: '10px',
                            padding: '10px',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '8px',
                        }}
                    >
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>
                            Точная настройка
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: '8px', alignItems: 'center' }}>
                            <label style={{ fontSize: '12px' }}>Повтор X</label>
                            <input
                                type="text"
                                value={String(safeAdvanced.repeat[0])}
                                onChange={(e) => onAdvancedChange?.({
                                    repeat: [
                                        clamp(toNum(e.target.value, 1), 0.01, 1000),
                                        safeAdvanced.repeat[1],
                                    ],
                                })}
                                disabled={disabled}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: '8px', alignItems: 'center' }}>
                            <label style={{ fontSize: '12px' }}>Повтор Y</label>
                            <input
                                type="text"
                                value={String(safeAdvanced.repeat[1])}
                                onChange={(e) => onAdvancedChange?.({
                                    repeat: [
                                        safeAdvanced.repeat[0],
                                        clamp(toNum(e.target.value, 1), 0.01, 1000),
                                    ],
                                })}
                                disabled={disabled}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: '8px', alignItems: 'center' }}>
                            <label style={{ fontSize: '12px' }}>Сдвиг X</label>
                            <input
                                type="text"
                                value={String(safeAdvanced.offset[0])}
                                onChange={(e) => onAdvancedChange?.({
                                    offset: [
                                        clamp(toNum(e.target.value, 0), -10, 10),
                                        safeAdvanced.offset[1],
                                    ],
                                })}
                                disabled={disabled}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: '8px', alignItems: 'center' }}>
                            <label style={{ fontSize: '12px' }}>Сдвиг Y</label>
                            <input
                                type="text"
                                value={String(safeAdvanced.offset[1])}
                                onChange={(e) => onAdvancedChange?.({
                                    offset: [
                                        safeAdvanced.offset[0],
                                        clamp(toNum(e.target.value, 0), -10, 10),
                                    ],
                                })}
                                disabled={disabled}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: '8px', alignItems: 'center' }}>
                            <label style={{ fontSize: '12px' }}>Поворот</label>
                            <input
                                type="text"
                                value={String(safeAdvanced.rotation)}
                                onChange={(e) => onAdvancedChange?.({
                                    rotation: clamp(toNum(e.target.value, 0), -6.28318, 6.28318),
                                })}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default React.memo(TextureTuningControls)